import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";

// // // //

interface SidebarContextState {
    isCollapsed: boolean;
    setIsCollapsed: (collapsed: boolean) => void;
    sidebarWidth: number;
}

const APP_SIDEBAR_WIDTH = 250;
const APP_SIDEBAR_WIDTH_COLLAPSED = 64;

/**
 * SidebarContext
 * Context for the Sidebar that encapsulates the collapsed state and width of the sidebar
 */
const SidebarContext = createContext<SidebarContextState | undefined>(
    undefined,
);

/**
 * SidebarProvider
 * Provider for the SidebarContext
 */
export function SidebarProvider({
    collapsed,
    children,
}: {
    collapsed?: boolean;
    children: ReactNode;
}) {
    const [isCollapsed, setIsCollapsed] = useState(collapsed ?? false);

    const sidebarWidth = isCollapsed
        ? APP_SIDEBAR_WIDTH_COLLAPSED
        : APP_SIDEBAR_WIDTH;

    // NOTE - app-sidebar-current-width is used to dynamically position the Modal component
    // when the sidebar is collapsed or expanded and when Modal.props.viewport is "inset"
    // We would typically use the SidebarContext + useSidebar() hook to get the current sidebar state,
    // but since we render the Modal component inside a React.Poral outside the SidebarContext provider,
    // we need to use a CSS variable to get the current sidebar width
    // // // //
    // We're esentially setting/unsetting a CSS variable value as a means to reflect global state,
    // IMHO this is an anti-pattern, but in the interest of velocity we'll keep this for now.
    // This pattern should not be repeated elsewhere in the codebase.
    // See Modal/index.tsx for implementation example. This should not be repeated elsewhere in the codebase.
    useEffect(() => {
        document.documentElement.style.setProperty(
            "--app-sidebar-current-width",
            `${sidebarWidth}px`,
        );

        return () => {
            document.documentElement.style.setProperty(
                "--app-sidebar-current-width",
                "0px",
            );
        };
    }, [sidebarWidth]);

    return (
        <SidebarContext.Provider
            value={{ isCollapsed, setIsCollapsed, sidebarWidth }}
        >
            {children}
        </SidebarContext.Provider>
    );
}

/**
 * useSidebar
 * React Hook that exposes the SidebarContext state and methods
 */
export function useSidebar() {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
}
